import { motion } from 'motion/react';
import { useIsDesktop } from '@firsttable/web-theme';
import { cn } from '@discover-lib/utils';
import { ToggleGroup, ToggleGroupItem, type ToggleGroupProps } from '@discover-ui/toggleGroup';
import useFavourite from '@discover-data/stores/hooks/favourite/useFavourite';
import { useAuthState } from '@/components/context/AuthContext';

export type DiscountOnlySwitchProps = Omit<
  ToggleGroupProps,
  'type' | 'value' | 'onValueChange' | 'defaultValue'
>;

export default function FavouriteOnlySwitch({ className, ...props }: DiscountOnlySwitchProps) {
  const { user } = useAuthState();
  const [favourite, setFavourite] = useFavourite(user?.favouriteIds);
  const isDesktop = useIsDesktop();

  return (
    <ToggleGroup
      type="single"
      value={favourite ? 'favouriteOnly' : 'all'}
      onValueChange={(value) => {
        setFavourite(value === 'favouriteOnly');
      }}
      kind="white-transparent"
      scale={isDesktop ? 'md' : 'sm'}
      aria-label="Toggle Favourites"
      className={cn('rounded-md bg-gray-100 p-1', className)}
      {...props}>
      <ToggleGroupItem
        value="favouriteOnly"
        aria-label="Toggle favourites"
        className="relative flex-1 bg-transparent font-medium text-blue data-[state=on]:bg-white data-[state=on]:text-blue data-[state=on]:shadow-sm">
        <span className="relative z-20">Favourites</span>
        {favourite && (
          <motion.span
            layoutId="bubble"
            className="absolute inset-0 z-10 rounded bg-white "
            transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
          />
        )}
      </ToggleGroupItem>
      <ToggleGroupItem
        value="all"
        aria-label="Toggle All"
        kind="white-transparent"
        scale={isDesktop ? 'md' : 'sm'}
        className="relative flex-1 bg-transparent font-medium text-blue data-[state=on]:bg-white data-[state=on]:text-blue data-[state=on]:shadow-sm">
        <span className="relative z-20">All</span>
        {!favourite && (
          <motion.span
            layoutId="bubble"
            className="absolute inset-0 z-10 rounded bg-white "
            transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
          />
        )}
      </ToggleGroupItem>
    </ToggleGroup>
  );
}
